/**
 * @license Copyright (c) 2014-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';

import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { Bold, Italic, Strikethrough, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { FontFamily, FontSize, FontColor } from '@ckeditor/ckeditor5-font';
import VariablesPlugin from './plugins/variables.plugin';
import FileUploadPlugin from './plugins/file-upload.plugin';
import { EditorConfig } from '@ckeditor/ckeditor5-core';
import { Variable } from '../../types/common';

interface TextFieldEditorConfig extends EditorConfig {
  fileUpload: {
    uploaderFn: (file: File) => Promise<string>;
  };
  variables: Variable[];
  styling: boolean;
}

const fontButtons = ['fontFamily', 'fontSize', 'fontColor'];
const textDecorationButtons = ['bold', 'italic', 'underline', 'strikethrough'];

class TextFieldEditor extends ClassicEditor {
  public static override defaultConfig = {
    plugins: [
      Essentials,
      Bold,
      Italic,
      Strikethrough,
      Underline,
      FontFamily,
      FontSize,
      FontColor,
      Paragraph,
      VariablesPlugin,
      FileUploadPlugin,
    ],
    language: 'en',
  };

  public static async create(sourceElementOrData: string | HTMLElement, config?: TextFieldEditorConfig): Promise<TextFieldEditor> {
    let toolbarItems = ['undo', 'redo'];

    if (config.styling) {
      toolbarItems = [...toolbarItems, '|', ...fontButtons, '|', ...textDecorationButtons];
    }

    if (config.variables) {
      toolbarItems = [...toolbarItems, '|', 'variables'];
    }

    if (config.fileUpload) {
      toolbarItems = [...toolbarItems, '|', 'fileupload'];
    }

    const editorConfig = {
      ...TextFieldEditor.defaultConfig,
      ...config,
      toolbar: {
        items: toolbarItems,
      },
    };

    const editor = await ClassicEditor.create(sourceElementOrData, editorConfig);

    editor.conversion.elementToElement({
      model: 'paragraph',
      view: 'div',
      converterPriority: 'high',
    });

    return editor;
  }
}

export default TextFieldEditor;
